import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from '@app/core/services/alert.service';
import { ApiService } from '@app/core/services/api.service';
import { FacilityTypesHelpers } from '@app/core/services/FacilityTypesHelper';
import { ObservableStore } from '@codewithdan/observable-store';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { MapContainerComponent } from '../components/map-container/map-container.component';
import { IAlternative } from '../interfaces/IAlternative';
import { ICMF } from '../interfaces/ICMF';
import { IContributingFactorCategory } from '../interfaces/IContributingFactorCategory';
import { ICountermeasure } from '../interfaces/ICountermeasure';
import { ICountermeasureLocation } from '../interfaces/ICountermeasureLocation';
import { ICrashTypeCategory } from '../interfaces/ICrashTypeCategory';
import {
  IInvestigation,
  IInvestigationNote,
} from '../interfaces/IInvestigation';
import { ILinkedCountermeasure } from '../interfaces/ILinkedCountermeasure';
import { IRecommendation } from '../interfaces/IRecommendation';
import { ISite } from '../interfaces/ISite';
import { ClearNotificationService } from '@app/core/services/notification.service';

export interface InvestigationState {
  investigation: IInvestigation;
  investigations: IInvestigation[];
  investigationList: IInvestigation[];
  countermeasures: ICountermeasure[];
  linkedCountermeasure: ILinkedCountermeasure;
  alternatives: IAlternative[];
  selectedAOI: any;
  aois: any[];
  sites: ISite[];
  mc: boolean; // map Loaded?
  recommendations: IRecommendation[];
  recommendationList: IRecommendation[];
  selectedCountermeasure: ILinkedCountermeasure;
  crashAnalysisCrashPatterns: ICrashTypeCategory[];
  crashAnalysisContributingFactors: IContributingFactorCategory[];
  linkedCountermeasures: ILinkedCountermeasure[];
  selectedRecommendation: IRecommendation;
  alternativeCountermeasures: ILinkedCountermeasure[];
  crashes: any[];
  crash: any;
  alternativeToEdit: IAlternative;
  siteMapURL: string;
  cmfs: ICMF[];
  selectedCMF: ICMF;
  linkedCountermeasureLocations: ICountermeasureLocation; // for investigation
  countermeasureLocations: ICountermeasureLocation; // for countermeasure
  startedCrashReview: boolean;

  // for test of proportions saving of dropdowns when moving inbetween tabs
  targetCrashTypes: any;
  targetCrashSeverities: any;
}

export enum InvestigationStateActions {
  InitializeState = 'initialize_state',
  CreateInvestigations = 'create_investigations',
  SelectInvestigation = 'select_investigation',
  MakeNewInvestigation = 'new_investigation',
  ResetInvestigation = 'reset_investigation',
  ClearInvestigation = 'clear_investigation',
  SaveInvestigation = 'save_investigation',
  SetCrashes = 'set_crashes',
  GetCrashes = 'get_crashes',
  CycleCrash = 'cycle_crash',
  ClearCrash = 'clear_crash',
  SetMap = 'set_map',
  AddSite = 'add_site',
  SetSites = 'set_sites',
  RemoveSite = 'remove_site',
  GetInvestigations = 'get_investigations',
  SetInvestigationList = 'set_investigation_list',
  GetCountermeasures = 'get_countermeasures',
  getRecommendations = 'get_recommendations',
  SetRecommendationList = 'set_recommendation_list',
  UpdateCountermeasures = 'update_countermeasures',
  AddAlternative = 'add_alternative',
  AddRecommendation = 'add_recommendation',
  SelectCountermeasure = 'select_countermeasure',
  ClearSelectedCountermeasure = 'clear_selected_countermeasure',
  AddLinkedCountermeasure = 'add_linked_countermeasure',
  SaveContributingCrashCategories = 'save_contributing_crash_categories',
  ResetCountermeasure = 'reset_countermeasure',
}
@Injectable({
  providedIn: 'root',
})
export class InvestigationService extends ObservableStore<InvestigationState> {
  mc: MapContainerComponent;
  ShowMeasureBackground: Subject<boolean> = new Subject();
  LoadStudyArea: Subject<boolean> = new Subject();
  statusChanged: Subject<any> = new Subject();
  getStudyArea: Subject<any> = new Subject();
  SiteData: ISite[] = [];

  investigation;
  /**This variable was created because the one in the database didn't seem to work correctly
   * This made it easier to keep track of the study area */
  public StudyAreaChanged: boolean = false;

  constructor(
    private router: Router,
    public api: ApiService,
    private route: ActivatedRoute,
    private FTHelpers: FacilityTypesHelpers,
    private alert: AlertService,
    private NotiService: ClearNotificationService
  ) {
    super({ trackStateHistory: true });
    const initialState = {
      crash: null,
      crashes: [],
      inves: null,
      investigations: [],
      investigationList: [],
      selectedAOI: null,
      aois: [],
      countermeasures: [],
      mc: false,
      sites: [],
      alternatives: [],
      recommendations: [],
      recommendationList: [],
      selectedCountermeasure: null,
      linkedCountermeasure: null,
      crashAnalysisContributingFactors: [],
      crashAnalysisCrashPatterns: [],
      linkedCountermeasures: [],
      selectedRecommendation: null,
      alternativeCountermeasures: [],
      alternativeToEdit: null,
      siteMapURL: null,
      cmfs: [],
      selectedCMF: null,
      linkedCountermeasureLocations: null,
      countermeasureLocations: null,
      targetCrashTypes: null,
      targetCrashSeverities: null,
      startedCrashReview: false,
    };
    this.setState(initialState, InvestigationStateActions.InitializeState);
  }

  setStartedCrashReview(value: boolean) {
    this.setState({ startedCrashReview: value }, null, false);
  }
  setMap(mc: MapContainerComponent) {
    this.mc = mc;
    this.setState({ mc: true }, InvestigationStateActions.SetMap);
  }

  async getInvestigations() {
    const investigations: IInvestigation[] = await this.api
      .getAllInvestigations()
      .toPromise();
    this.setState(
      { investigations },
      InvestigationStateActions.GetInvestigations
    );
    return investigations;
  }

  setInvestigationList(investigationList: IInvestigation[]) {
    this.setState(
      { investigationList },
      InvestigationStateActions.SetInvestigationList
    );
  }

  clearTestOfProportionsDropdowns() {
    this.setState({ targetCrashSeverities: null, targetCrashTypes: null });
  }

  setTestOfProportionsDropdowns(
    targetCrashSeverities?: any,
    targetCrashTypes?: any
  ) {
    if (targetCrashSeverities) {
      this.setState({ targetCrashSeverities }, null, false);
    }
    if (targetCrashTypes) {
      this.setState({ targetCrashTypes }, null, false);
    }
  }

  async getCountermeasures() {
    const countermeasures: ICountermeasure[] = await this.api
      .getCountermeasures()
      .toPromise();
    this.setState(
      { countermeasures },
      InvestigationStateActions.GetCountermeasures
    );
  }

  addCountermeasure(countermeasure: ICountermeasure) {
    const countermeasures = this.getState().countermeasures;
    countermeasures.push(countermeasure);
    this.setState(
      { countermeasures },
      InvestigationStateActions.UpdateCountermeasures
    );
  }

  async addRecommendation(rec: IRecommendation, investigationID: number) {
    const recommendation = await this.api
      .saveRecommendation(rec, investigationID)
      .toPromise();
    const recommendations = this.getState().recommendations;
    recommendations.push(recommendation);
    this.setState(
      { recommendations },
      InvestigationStateActions.AddRecommendation
    );
  }

  async updateRecommendation(rec: IRecommendation, recommendationID: number) {
    const investigationID = this.getState().investigation.InvestigationID;
    this.api
      .updateRecommendation(investigationID, rec, recommendationID)
      .toPromise()
      .then(() => this.getRecommendations());
  }

  async getRecommendations() {
    const id = this.getState().investigation.InvestigationID;
    const recommendations = await this.api
      .getRecommendationsByID(id)
      .pipe(take(1))
      .toPromise();
    const cms = await this.getLinkedCountermeasures();

    recommendations.forEach((rec, i, array) => {
      const cm = cms.find(
        (_cm) => _cm.ID === rec.xrefInvestigationCountermeasureID
      );
      if (cm) array[i]['LinkedCountermeasure'] = cm;
    });

    this.setState(
      { recommendations },
      InvestigationStateActions.getRecommendations
    );
    return recommendations;
  }

  setRecommendationList(recommendationList: IRecommendation[]) {
    this.setState(
      { recommendationList },
      InvestigationStateActions.SetRecommendationList
    );
  }

  selectCountermeasure(countermeasure: ILinkedCountermeasure) {
    const selectedCountermeasure = countermeasure;
    this.setState(
      { selectedCountermeasure },
      InvestigationStateActions.SelectCountermeasure
    );
  }

  clearRecommendation() {
    this.setState(
      { selectedCountermeasure: null, selectedRecommendation: null },
      InvestigationStateActions.ClearSelectedCountermeasure
    );
  }

  async addLinkedCountermeasure(linkedCountermeasure: ILinkedCountermeasure) {
    let cm: any;
    const selectedCountermeasureID = this.getState().selectedCountermeasure?.ID;

    selectedCountermeasureID
      ? (cm = await this.api
          .updateLinkedCountermeasure([linkedCountermeasure])
          .pipe(take(1))
          .toPromise())
      : (cm = await this.api
          .saveLinkedCountermeasure(linkedCountermeasure)
          .pipe(take(1))
          .toPromise());

    //console.log(cm);
    if (selectedCountermeasureID)
      this.setState(
        { selectedCountermeasure: cm[0] },
        InvestigationStateActions.AddLinkedCountermeasure
      );
    else
      this.setState(
        { selectedCountermeasure: cm[0] },
        InvestigationStateActions.AddLinkedCountermeasure
      );
  }

  // grabbing all countermeasure locations for specific countermeasure
  async getLinkedCountermeasureLocation(xrefCountermeasureID?: number) {
    let selectedCountermeasureID = this.getState().selectedCountermeasure?.ID;
    if (xrefCountermeasureID) selectedCountermeasureID = xrefCountermeasureID;

    if (selectedCountermeasureID) {
      const countermeasureLocations = await this.api
        .getCountermeasureLocation(selectedCountermeasureID)
        .pipe(take(1))
        .toPromise();
      this.setState({ countermeasureLocations });
      return countermeasureLocations;
    } else {
      console.warn('No selected countermeasure!');
    }
  }

  // grabbing all countermeasure locations for investigation
  async getLinkedCountermeasureLocations() {
    const linkedCountermeasureLocations = await this.api
      .getLinkedCountermeasureLocationsByInvestigation(this.InvestigationID)
      .pipe(take(1))
      .toPromise();
    this.setState({ linkedCountermeasureLocations });
    return linkedCountermeasureLocations;
  }

  async saveLinkedCountermeasureLocation(location: ICountermeasureLocation) {
    const selectedCountermeasureID = this.getState().selectedCountermeasure?.ID;
    if (selectedCountermeasureID) {
      location.LinkedCountermeasureID = selectedCountermeasureID;
      return this.api
        .saveCountermeasureLocation(location)
        .pipe(take(1))
        .toPromise();
    } else {
      console.warn('No selected countermeasure!');
    }
  }

  async updateLinkedCountermeasureLocation(location: ICountermeasureLocation) {
    const selectedCountermeasureID = this.getState().selectedCountermeasure?.ID;
    if (selectedCountermeasureID) {
      location.LinkedCountermeasureID = selectedCountermeasureID;
      return this.api
        .updateCountermeasureLocation(selectedCountermeasureID, location)
        .pipe(take(1))
        .toPromise();
    } else {
      console.warn('No selected countermeasure!');
    }
  }

  async getCountermeasureLocationCrashCounts(
    geometry: __esri.Geometry,
    noBufferGeometry: __esri.Geometry
  ) {
    const id = this.getState().investigation.InvestigationID;
    return this.api
      .getCountermeasureLocationCrashCounts(id, geometry, noBufferGeometry)
      .pipe(take(1))
      .toPromise();
  }

  resetCountermeasure() {
    this.setState(
      { linkedCountermeasure: null },
      InvestigationStateActions.ResetCountermeasure
    );
  }

  clearInvestigatation() {
    this.setState(
      {
        investigation: null,
        linkedCountermeasures: [],
        linkedCountermeasure: null,
        aois: [],
      },
      InvestigationStateActions.ClearInvestigation
    );
  }

  async getInvestigation(id: number | string) {
    if (id === 'new') {
      const newInvestigation: IInvestigation = {
        CreatedBy: sessionStorage.getItem('UserName') || 'User',
        InvestigationToUser: []
      };
      this.setState(
        { investigation: newInvestigation },
        InvestigationStateActions.SelectInvestigation
      );
      return newInvestigation;
    }

    try {
      const investigation = await this.api
        .getInvestigation(id)
        .pipe(take(1))
        .toPromise();
      const linkedCountermeasures = await this.api
        .getLinkedCountermeasures(investigation.InvestigationID)
        .pipe(take(1))
        .toPromise();

      if (investigation) {
        this.setState(
          { investigation, linkedCountermeasures },
          InvestigationStateActions.SelectInvestigation
        );
        return investigation;
      }
    } catch (err) {
      this.router.navigate(['/site-analysis/inves-rec/investigations']);
    }

    return null;
  }

  async getLinkedCountermeasures(withLocations?: boolean) {
    const linkedCountermeasures = await this.api
      .getLinkedCountermeasures(this.InvestigationID)
      .pipe(take(1))
      .toPromise();
    await this.FTHelpers.loadFacilityTypes();
    if (withLocations) {
      const location = await this.api
        .getLinkedCountermeasureLocationsByInvestigation(this.InvestigationID)
        .pipe(take(1))
        .toPromise();
      [
        ...location.Segments,
        ...location.Intersections,
        ...location.Ramps,
      ].forEach((location) => {
        let facilityType = this.FTHelpers.facilityTypes.find(
          (fac) => fac.FacilityTypeID === location.FacilityTypeID
        );
        if (!facilityType) {
          facilityType = this.FTHelpers.TestChildFacilityType(
            location.FacilityTypeID
          );
        }
        if (facilityType)
          location['FacilityTypeDescription'] = facilityType.Description;
        const i = linkedCountermeasures.findIndex(
          (cm) => cm.ID === location.InvestigationCountermeasureID
        );
        if (!linkedCountermeasures[i].Locations)
          linkedCountermeasures[i].Locations = [];
        if (i > -1) linkedCountermeasures[i].Locations.push(location);
      });
    }

    this.setState({ linkedCountermeasures });
    return linkedCountermeasures;
  }

  async saveInvestigation(investigation: IInvestigation) {
    try {
      investigation.CreatedBy = sessionStorage.getItem('UserName');
      investigation.UpdatedBy = sessionStorage.getItem('UserName');
      const newInvestigation = await this.api
        .saveNewInvestigation(investigation)
        .toPromise();
      this.replaceHistory(newInvestigation.InvestigationID);
      this.setState(
        { investigation: newInvestigation },
        InvestigationStateActions.SaveInvestigation
      );
    } catch (err) {
      throw err;
    }
  }

  private replaceHistory(investigationID: number) {
    const url = window.location.href.replace('new', `${investigationID}`);
    history.replaceState(null, null, url);
  }

  async updateInvestigation(investigation: IInvestigation) {
    try {
      investigation.UpdatedBy = sessionStorage.getItem('UserName');
      const UpdatedInvestigation = await this.api
        .updateInvestigation(investigation)
        .toPromise();
      const Investigation = await this.getInvestigation(
        investigation.InvestigationID
      );
      this.setState(
        { investigation: Investigation },
        InvestigationStateActions.SaveInvestigation
      );
    } catch (err) {
      throw err;
    }
  }

  async completeInvestigation(comment: string) {
    try {
      const investigation = await this.api
        .completeInvestigation(comment, this.InvestigationID)
        .toPromise();
      this.setState(
        { investigation },
        InvestigationStateActions.SaveInvestigation
      );
      this.statusChanged.next(investigation.Status);
    } catch (err) {
      throw err;
    }
  }

  async approveInvestigation(comment: string) {
    try {
      const investigation = await this.api
        .approveInvestigation(comment, this.InvestigationID)
        .toPromise();
      this.setState(
        { investigation },
        InvestigationStateActions.SaveInvestigation
      );
      this.statusChanged.next(investigation.Status);
    } catch (err) {
      throw err;
    }
  }

  async getCMFs(countermeasure: ILinkedCountermeasure) {
    if (!countermeasure.CountermeasureID || !countermeasure.FacilityTypeID)
      return;
    try {
      const cmfs = await this.api
        .getCMFS(countermeasure.FacilityTypeID, countermeasure.CountermeasureID)
        .toPromise();
      this.setState({ cmfs });
    } catch (err) {
      throw err;
    }
  }

  resetInvestigation() {
    this.setState(
      { investigation: null, aois: [], crashes: [] },
      InvestigationStateActions.ResetInvestigation
    );
  }

  addSite(site: ISite, ScreeningSite: boolean = false) {
    const investigation = this.getState().investigation;
    if (!investigation.InvestigationSites)
      investigation.InvestigationSites = [];
    const index = investigation.InvestigationSites.findIndex(
      (item) =>
        item.ID === site.ID &&
        item.SegmentID === site.SegmentID &&
        item.MasterIntersectionID === site.MasterIntersectionID &&
        item.RampID === site.RampID &&
        item.ScreenedSegmentID === site.ScreenedSegmentID &&
        item.ScreenedIntersectionID === site.ScreenedIntersectionID &&
        item.ScreenedRampID === site.ScreenedRampID &&
        item.EventSegmentID === site.EventSegmentID
    );
    if (index === -1 || ScreeningSite) {
      site.graphic = site.graphic.toJSON() as any;
      investigation.InvestigationSites.push(site);
      this.setState({ investigation }, InvestigationStateActions.AddSite);
    } else {
      this.NotiService.showNotification({
        type: 'warning',
        duration: 4000,
        message: 'Site Already In List',
      });
    }
  }

  async addAlternative(name: string, note: string) {
    const countermeasureIds = this.getState().alternativeCountermeasures.map(
      (cm) => cm.ID
    );
    const existingAlternativeID =
      this.getState().alternativeToEdit?.AlternativeID ?? 0;

    const alternative: IAlternative = {
      Name: name,
      Note: note,
      CountermeasureIds: countermeasureIds,
      AlternativeID: existingAlternativeID,
    };

    await this.api
      .addAlternative(alternative, this.InvestigationID)
      .toPromise();
  }

  async copyAlternative(alternativeId: number, newName: string) {
    await this.api
      .copyAlternative(alternativeId, this.InvestigationID, newName)
      .toPromise();
  }

  async removeAlternative(alternativeId: number) {
    await this.api
      .removeAlternative(this.InvestigationID, alternativeId)
      .toPromise();
  }

  async editAlternative(alternativeToEdit: IAlternative) {
    const alternativeCountermeasures = await this.api
      .getAlternativeCountermeasures(alternativeToEdit.AlternativeID)
      .toPromise();
    this.setState({ alternativeToEdit, alternativeCountermeasures });
  }

  async getAlternatives() {
    const investigationId = this.getState().investigation.InvestigationID;
    const alternatives = await this.api
      .getAlternatives(investigationId)
      .toPromise();
    const alternativeLinkedCountermeasures = await this.api
      .getLinkedCountermeasuresForAlternatives(investigationId)
      .toPromise();

    for (const cm of alternativeLinkedCountermeasures) {
      const index = alternatives.findIndex(
        (alt) => alt.AlternativeID === cm.AlternativeID
      );
      if (index > -1) {
        if (!alternatives[index].LinkedCountermeasures) {
          alternatives[index].LinkedCountermeasures = [];
        }
        alternatives[index].LinkedCountermeasures.push(cm);
      }
    }
    this.setState({ alternatives });
    return alternatives;
  }

  resetAlternativeToEdit() {
    this.setState({ alternativeToEdit: null, alternativeCountermeasures: [] });
  }

  setSites(sites: ISite[]) {
    sites.forEach((site) => {
      try {
        site.graphic = site.graphic.toJSON() as any;
      } catch (err) {
        //Catch this when toJSON is not a method
      }
    });
    const investigation = this.getState().investigation;
    if (investigation) {
      investigation.InvestigationSites = sites;
      //Remove this line because since I am not making the graphic be JSON anymore it is failing
      this.setState({ investigation }, InvestigationStateActions.SetSites);
    }
  }

  removeSite(site: ISite) {
    const investigation = this.getState().investigation;
    const index = investigation.InvestigationSites.findIndex(
      (item) =>
        item.ID === site.ID &&
        item.SegmentID === site.SegmentID &&
        item.MasterIntersectionID === site.MasterIntersectionID &&
        item.RampID === site.RampID &&
        item.EventSegmentID === site.EventSegmentID &&
        item.ScreenedSegmentID === site.ScreenedSegmentID &&
        item.ScreenedRampID == site.ScreenedRampID &&
        item.ScreenedIntersectionID == site.ScreenedIntersectionID
    );
    if (index !== -1) {
      investigation.InvestigationSites.splice(index, 1);
      this.setState({ investigation }, InvestigationStateActions.RemoveSite);
    }
  }

  setAOIs(aois: any[]) {
    aois = aois.map((aoi) => aoi.toJSON());
    this.setState({ aois });
  }

  async saveAOIS(geometryArray: any[]) {
    const id = this.getState().investigation.InvestigationID;
    try {
      await this.api.saveAOIS(geometryArray, id).toPromise();
      await this.getInvestigation(id);
    } catch (err) {
      throw err;
    }
  }

  loadAOIS() {
    const id = this.getState().investigation.InvestigationID;
    try {
      return this.api.loadAOIS(id).pipe(take(1));
    } catch (err) {
      throw err;
    }
  }

  selectCMFToUpdate(CMF: ICMF) {
    this.setState({ selectedCMF: CMF });
  }

  selectRecommendation(selectedRecommendation: IRecommendation) {
    this.setState({ selectedRecommendation });
    //console.log(selectedRecommendation);
  }

  // used in alternative countermeasure process for grid
  updateAlternativeCountermeasures(
    alternativeCountermeasures: ILinkedCountermeasure[]
  ) {
    setTimeout(() => {
      this.setState({ alternativeCountermeasures });
    }, 100);
  }

  async getCrashes() {
    const investigationId = this.getState().investigation.InvestigationID;
    try {
      const crashes = await this.api.getCrashes(investigationId).toPromise();
      this.setState({ crashes }, 'get_crashes');
      return crashes;
    } catch (err) {
      throw err;
    }
  }

  async updateCrashes() {
    const investigation = this.getState().investigation;
    try {
      // Removing this so studyArea will not be changed
      // set it to true just incase user changes step without leaving the investigation
      // investigation.StudyAreaChanged = true;
      //this.setState({ investigation });
      const response = await this.api
        .updateCrashes(investigation.InvestigationID)
        .toPromise();
      return response;
    } catch (err) {
      throw err;
    }
  }

  selectCrash(crash: any) {
    this.setState({ crash });
  }

  async updateStep(stepNumber: number) {
    const investigationId = this.getState().investigation.InvestigationID;
    try {
      const investigation = await this.api
        .updateStep(investigationId, stepNumber)
        .toPromise();
      this.setState({ investigation });
    } catch (err) {
      throw err;
    }
  }
  public async finalizeInvestigation(finalizeNote: IInvestigationNote) {
    await this.api
      .finalizeInvestigation(finalizeNote, this.InvestigationID)
      .toPromise();
    const investigation = this.getStateProperty(
      'investigation'
    ) as IInvestigation;
    investigation.Status = 4;
    investigation.LastStepCompleted = 4;
    this.setState({ investigation });
  }

  async saveCMFs(CMFs: ICMF[]) {
    const xrefInvestigationCountermeasureID =
      this.getState().selectedCountermeasure.ID;
    const CMFsToSave = [];
    CMFs.forEach((cmf) => {
      const cmfToSave = {
        xrefInvestigationCountermeasureID,
        ModificationFactor: cmf.ModificationFactor,
        ReductionFactor: cmf.ReductionFactor,
        ExpectedCrashesWithoutCMF: cmf.ExpectedCrashesWithout,
        ExpectedCrashesWithMF: cmf.ExpectedCrashesWith,
        Severity: cmf.Severity,
        IsUserDefined: cmf.IsUserDefined ? true : false,
        FacilityTypeID: cmf.FacilityTypeID,
        IsDerived: cmf.IsDerived,
      };
      CMFsToSave.push(cmfToSave);
    });
    try {
      await this.api.saveCMFs(this.InvestigationID, CMFsToSave).toPromise();
      return true;
    } catch (err) {
      throw err;
    }
  }

  updateSiteMapURL(url: string) {
    this.setState({ siteMapURL: url });
  }

  get StartedCrashReview() {
    return this.getStateProperty('startedCrashReview') as boolean;
  }

  get CMFs() {
    return this.getStateProperty('cmfs') as ICMF[];
  }

  get Investigation() {
    return this.getStateProperty('investigation') as IInvestigation;
  }

  get InvestigationID() {
    return (this.getStateProperty('investigation') as IInvestigation)
      ?.InvestigationID;
  }

  get InvestigationStatus() {
    return (this.getStateProperty('investigation') as IInvestigation).Status;
  }

  get InvestigationApproved() {
    return (
      (this.getStateProperty('investigation') as IInvestigation).Status === 5
    );
  }
  get InvestigationCompletedApproved() {
    const status = (this.getStateProperty('investigation') as IInvestigation)
      ?.Status;
    if (status >= 4) {
      return true;
    }
    return false;
  }

  get InvestigationCompleted() {
    return (
      (this.getStateProperty('investigation') as IInvestigation).Status === 4
    );
  }

  get isDesignAlternative() {
    return (this.getStateProperty('investigation') as IInvestigation)
      .isDesignAlternative;
  }

  get InvestigationSites() {
    const sites = (this.getStateProperty('investigation') as IInvestigation)
      .InvestigationSites;
    if (!sites) return [];
    sites
      .filter((site) => site.graphic)
      .forEach(
        (site) =>
          (site.graphic = this.mc.map._mapService.instGraphic.fromJSON(
            site.graphic
          ))
      );
    return sites;
  }

  get LastStepCompleted() {
    return (this.getStateProperty('investigation') as IInvestigation)
      .LastStepCompleted;
  }

  get AOIs() {
    const aois = (this.getStateProperty('aois') as any[]).map((aoi) =>
      this.mc.map._mapService.instGraphic.fromJSON(aoi)
    );
    return aois;
  }

  get TargetCrashSeverities() {
    return this.getStateProperty('targetCrashSeverities') as any;
  }

  get TargetCrashTypes() {
    return this.getStateProperty('targetCrashTypes') as any;
  }

  get Map() {
    return this.mc;
  }

  get Recommendations() {
    return this.getStateProperty('recommendations') as IRecommendation[];
  }

  get Crashes() {
    return this.getStateProperty('crashes') as any[];
  }

  get InvestigationDateDifference() {
    const investigation = this.getStateProperty(
      'investigation'
    ) as IInvestigation;
    const a = moment(investigation.ToDate);
    const b = moment(investigation.FromDate);
    return parseFloat(a.diff(b, 'years', true).toFixed(1));
  }

  get CrashSeveritiesDescription() {
    let severities = '';
    const { StudyK, StudyA, StudyB, StudyC, StudyO } = this.Investigation;

    if (StudyK && StudyA && StudyB && StudyC && StudyO) {
      severities = 'Total Crashes';
      return severities;
    }

    if (StudyK && StudyA && StudyB && StudyC) {
      severities = 'Fatal and Injury';
      return severities;
    }

    if (StudyK) severities = 'Fatal';
    if (StudyA) severities += ', Serious';
    if (StudyB) severities += ', Non-Serious';
    if (StudyC) severities += ', Possible Injury';
    if (StudyO) severities += ', PDO';
    return severities;
  }
}
